'use client';
import Badge from '@/components/Badge';
import { ButtonProps } from '@/components/Button/Button';
import { Modal } from '@/components/Modal/Modal';
import Table, { PartialRowData } from '@/components/Table/Table';
import CheckCircle from '@/components/icons/CheckCircle';
import CrossCircle from '@/components/icons/CrossCircle';
import PlusCircle from '@/components/icons/PlusCircle';
import Profile from '@/components/icons/User/ProfileIcon';
import { NotifsContext } from '@/contexts';
import useNavigate from '@/hooks/useNavigate';
import {
  ChangeEmpStatusReq,
  DeleteEmpReq,
  useChangeEmpStatusMutation,
  useDeleteEmpMutation,
  useExportEmployeesMutation,
  useLazyGetListEmployeesQuery,
} from '@/services/API';
import { useAppSelector } from '@/store/hooks';
import { AccountStatus, IEmployee } from '@/types';
import { getNumberOfDaysSince } from '@/utils/dates';
import getEnv from '@/utils/env';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext, useEffect, useRef, useState } from 'react';

type EmployeeTableData = Pick<
  IEmployee,
  | 'id'
  | 'sn'
  | 'given_name'
  | 'email_address'
  | 'mobile'
  | 'account_status'
  | 'last_login_time'
> &
  PartialRowData;

export default function Page() {
  const [openedDeleteModal, setOpenedDeleteModal] = useState(false);
  const [openedEnableModal, setOpenedEnableModal] = useState(false);
  const [openedDisableModal, setOpenedDisableModal] = useState(false);
  const [openedBlockModal, setOpenedBlockModal] = useState(false);
  const [openedUnblockModal, setOpenedUnblockModal] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<
    EmployeeTableData[]
  >([]);

  const navigate = useNavigate();

  const profil = getEnv().PROFIL;
  const selectedSte = useAppSelector((state) => state.selectedSte.societe);
  const [tableData, setTableData] = useState<EmployeeTableData[]>([]);
  const [deleteEmployees, { isLoading: isLoadingDelete }] =
    useDeleteEmpMutation();
  const [getListEmployees, { data: emps, isLoading: isLoadingGetList }] =
    useLazyGetListEmployeesQuery();
  const [changeEmpStatus, { isLoading: isLoadingStatus }] =
    useChangeEmpStatusMutation();
  const [exportEmployees, { isLoading: isExporting }] =
    useExportEmployeesMutation();
  const columnHelper = createColumnHelper<EmployeeTableData>();

  function renderIcon(value?: AccountStatus) {
    switch (value) {
      case AccountStatus.ACTIVE:
        return <Badge variant="success" icon={<CheckCircle />} label="Actif" />;
      case AccountStatus.DISABLED_PARTNER:
        return (
          <Badge variant="secondary" icon={<Profile />} label="Désactivé" />
        );

      case AccountStatus.DISABLED_BNPPF:
        return <Badge variant="danger" icon={<CrossCircle />} label="Bloqué" />;

      default:
        return '-';
    }
  }

  const cols = [
    columnHelper.accessor('sn', {
      header: () => 'Nom',
      meta: { filterVariant: 'text' },
    }),
    columnHelper.accessor('given_name', {
      header: () => 'Prénom',
      meta: { filterVariant: 'text' },
    }),
    columnHelper.accessor('email_address', {
      header: () => 'Adresse e-mail',
      enableColumnFilter: false,
      enableSorting: false,
    }),
    columnHelper.accessor('account_status', {
      header: () => 'Statut',
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ getValue }) => renderIcon(getValue()),
    }),
    columnHelper.accessor('last_login_time', {
      header: () => 'Dernière connexion',
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ getValue }) =>
        getValue()
          ? '' +
            getNumberOfDaysSince(getValue() + '') +
            ' Jour' +
            (getNumberOfDaysSince(getValue() + '') > 1 ? 's' : '')
          : '-',
    }),
  ] as ColumnDef<EmployeeTableData>[];
  const { addToast } = useContext(NotifsContext);

  function deleteEmps() {
    if (selectedEmployees.length) {
      let req = {
        siren: selectedSte.siren,
        resources: selectedEmployees.map<DeleteEmpReq>(({ id }) => ({
          id,
        })),
      };
      deleteEmployees(req)
        .unwrap()
        .then(() => {
          addToast &&
            addToast({
              toastType: 'success',
              children: 'Confirmation de suppression.',
            });
          getListEmployees(selectedSte.siren);
        })
        .catch(() => {
          addToast &&
            addToast({
              toastType: 'error',
              children: `Une erreur est survenue.
            Rééssayer ultérieurement.`,
            });
        })
        .finally(() => setOpenedDeleteModal(false));
    }
  }
  function changeStatus(
    newStatus: AccountStatus,
    msg: string,
    modalHook: (state: boolean) => void
  ) {
    if (selectedEmployees.length) {
      let req = {
        siren: selectedSte.siren,
        resources: selectedEmployees.map<ChangeEmpStatusReq>(({ id }) => ({
          employee_id: id,
          status: newStatus,
        })),
      };
      changeEmpStatus(req)
        .unwrap()
        .then(() => {
          addToast &&
            addToast({
              toastType: 'success',
              children: msg,
            });
          getListEmployees(selectedSte.siren);
        })
        .catch(() => {
          addToast &&
            addToast({
              toastType: 'error',
              children: `Une erreur est survenue.
            Rééssayer ultérieurement.`,
            });
        })
        .finally(() => modalHook(false));
    }
  }

  const edit = (row: EmployeeTableData) => {
    navigate('employees/modifier/?id=' + row.id);
  };

  useEffect(() => {
    if (emps) {
      setTableData(
        emps.map<EmployeeTableData>(
          ({
            sn,
            id,
            given_name,
            email_address,
            mobile,
            account_status,
            last_login_time,
          }) => ({
            sn,
            id,
            given_name,
            email_address,
            mobile,
            account_status,
            last_login_time,
            disable:
              (profil === 'PARTENAIRE' &&
                account_status === AccountStatus.DISABLED_BNPPF) ||
              (profil === 'PF' &&
                account_status === AccountStatus.DISABLED_PARTNER)
                ? true
                : false,
          })
        )
      );
    }
  }, [emps]);

  useEffect(() => {
    getListEmployees(selectedSte.siren);
  }, []);

  const hiddenLink = useRef<HTMLAnchorElement>(null);

  function onExport() {
    exportEmployees()
      .unwrap()
      .then((data) => {
        const file = new Blob(
          [
            new Uint8Array([0xef, 0xbb, 0xbf]), // UTF-8 BOM
            data,
          ],
          { type: 'text/csv;charset=utf-8' }
        );
        if (hiddenLink.current) {
          hiddenLink.current.href = URL.createObjectURL(file);
          hiddenLink.current.click();
        }
        addToast &&
          addToast({
            toastType: 'success',
            children: "Confirmation de l'export.",
          });
      })
      .catch((err) => {
        addToast &&
          addToast({
            toastType: 'error',
            children: `Une erreur est survenue.
        Rééssayer ultérieurement.`,
          });
      });
  }

  const leftbtns: ButtonProps[] = [
    {
      variant: 'secondary',
      children: 'Importer',
      onClick: () => navigate('employees/importer'),
    },
    {
      variant: 'secondary',
      disabled: !emps || emps.length === 0,
      children: 'Exporter',
      loading: isExporting,
      onClick: onExport,
    },
  ];

  const rightbtns: ButtonProps[] = [
    profil === 'PF'
      ? {
          variant: 'secondary',
          onClick: () => setOpenedBlockModal(true),
          disabled:
            selectedEmployees.length === 0 ||
            selectedEmployees.some(
              (emp) => emp.account_status === AccountStatus.DISABLED_BNPPF
            ),
          children: 'Bloquer',
        }
      : {
          variant: 'secondary',
          onClick: () => setOpenedEnableModal(true),
          disabled:
            selectedEmployees.length === 0 ||
            selectedEmployees.some(
              (emp) => emp.account_status === AccountStatus.ACTIVE
            ),
          children: 'Activer',
        },

    profil === 'PF'
      ? {
          variant: 'secondary',
          onClick: () => setOpenedUnblockModal(true),
          disabled:
            selectedEmployees.length === 0 ||
            selectedEmployees.some(
              (emp) => emp.account_status === AccountStatus.ACTIVE
            ),
          children: 'Débloquer',
        }
      : {
          variant: 'secondary',
          onClick: () => setOpenedDisableModal(true),
          disabled:
            selectedEmployees.length === 0 ||
            selectedEmployees.some(
              (emp) => emp.account_status === AccountStatus.DISABLED_PARTNER
            ),
          children: 'Désactiver',
        },
    {
      disabled: selectedEmployees.length === 0,
      onClick: () => setOpenedDeleteModal(true),
      variant: 'secondary',
      children: 'Supprimer',
    },
    {
      onClick: () => navigate('employees/creer'),
      icon: <PlusCircle />,
      children: 'Ajouter',
    },
  ];

  return (
    <>
      {/* @ts-ignore */}
      <Table<EmployeeTableData>
        title="Liste des employés"
        leftbtns={leftbtns}
        rightbtns={rightbtns}
        isLoadingData={isLoadingGetList}
        onEdit={edit}
        columns={cols}
        columnsSort={[{ id: 'sn', desc: false }]}
        data={tableData}
        enableFilter={true}
        onSelectionChange={setSelectedEmployees}
      />
      <Modal
        primaryBtnProps={{
          loading: isLoadingDelete,
          children: 'Confirmer',
          onClick: deleteEmps,
        }}
        secondaryBtnProps={{
          children: 'Annuler',
          onClick: () => setOpenedDeleteModal(false),
        }}
        title={
          selectedEmployees.length > 1
            ? 'Voulez-vous supprimer les employés ?'
            : "Voulez-vous supprimer l'employé ?"
        }
        open={openedDeleteModal}
        onClose={() => setOpenedDeleteModal(false)}
      />
      <Modal
        primaryBtnProps={{
          loading: isLoadingStatus,
          children: 'Confirmer',
          onClick: () => {
            changeStatus(
              AccountStatus.ACTIVE,
              "Confirmation d'activation.",
              setOpenedEnableModal
            );
          },
        }}
        secondaryBtnProps={{
          children: 'Annuler',
          onClick: () => setOpenedEnableModal(false),
        }}
        title={
          selectedEmployees.length > 1
            ? 'Voulez-vous activer les employés ?'
            : "Voulez-vous activer l'employé ?"
        }
        open={openedEnableModal}
        onClose={() => setOpenedEnableModal(false)}
      />
      <Modal
        primaryBtnProps={{
          loading: isLoadingStatus,
          children: 'Confirmer',
          onClick: () => {
            changeStatus(
              AccountStatus.DISABLED_PARTNER,
              'Confirmation de désactivation.',
              setOpenedDisableModal
            );
          },
        }}
        secondaryBtnProps={{
          children: 'Annuler',
          onClick: () => setOpenedDisableModal(false),
        }}
        title={
          selectedEmployees.length > 1
            ? 'Voulez-vous désactiver les employés ?'
            : "Voulez-vous désactiver l'employé ?"
        }
        open={openedDisableModal}
        onClose={() => setOpenedDisableModal(false)}
      />
      <Modal
        primaryBtnProps={{
          loading: isLoadingStatus,
          children: 'Confirmer',
          onClick: () => {
            changeStatus(
              AccountStatus.DISABLED_BNPPF,
              'Confirmation de bloquage.',
              setOpenedBlockModal
            );
          },
        }}
        secondaryBtnProps={{
          children: 'Annuler',
          onClick: () => setOpenedBlockModal(false),
        }}
        title={
          selectedEmployees.length > 1
            ? 'Voulez-vous bloquer les employés ?'
            : "Voulez-vous bloquer l'employé ?"
        }
        open={openedBlockModal}
        onClose={() => setOpenedBlockModal(false)}
      />
      <Modal
        primaryBtnProps={{
          loading: isLoadingStatus,
          children: 'Confirmer',
          onClick: () => {
            changeStatus(
              AccountStatus.ACTIVE,
              'Confirmation de débloquage.',
              setOpenedUnblockModal
            );
          },
        }}
        secondaryBtnProps={{
          children: 'Annuler',
          onClick: () => setOpenedUnblockModal(false),
        }}
        title={
          selectedEmployees.length > 1
            ? 'Voulez-vous débloquer les employés ?'
            : "Voulez-vous débloquer l'employé ?"
        }
        open={openedUnblockModal}
        onClose={() => setOpenedUnblockModal(false)}
      />
      <a
        ref={hiddenLink}
        download="export_employés.csv"
        style={{ display: 'none' }} // Make the file input element invisible
      />
    </>
  );
}
